import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';

const LegalPage = ({ titleEn, titleAr, lastUpdatedEn, lastUpdatedAr, seo, children }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const title = isAr ? titleAr : titleEn;
  const lastUpdated = isAr ? lastUpdatedAr : lastUpdatedEn;
  return (
    <>
      {seo && (
        <SEO
          titleEn={seo.titleEn}
          titleAr={seo.titleAr}
          descEn={seo.descEn}
          descAr={seo.descAr}
          path={seo.path}
        />
      )}
      <div className="pt-16">
        <section className="py-20 lg:py-24 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-2">{title}</h1>
              <p className="text-sm text-muted-foreground">{lastUpdated}</p>
            </div>
            <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
              {children}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-base font-semibold text-foreground mt-8 mb-3">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const CookiePage = () => (
  <LegalPage
    titleEn="Cookie Policy"
    titleAr="سياسة الكوكيز"
    lastUpdatedEn="Last updated: January 1, 2025"
    lastUpdatedAr="آخر تحديث: ١ يناير ٢٠٢٥"
    seo={{
      titleEn: 'Cookie Policy | Quidory',
      titleAr: 'سياسة الكوكيز | كويدوري',
      descEn: "Learn about Quidory's use of cookies and how to manage your cookie preferences on our platform.",
      descAr: 'تعرف على استخدام كويدوري لملفات تعريف الارتباط (الكوكيز) وكيفية إدارة تفضيلاتك على منصتنا.',
      path: '/cookie-policy',
    }}
  >
    <Section title="1. What Are Cookies?">
      <p>Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work efficiently and to provide information to the website owner.</p>
      <p>Quidory uses cookies to enhance your experience on our platform, remember your preferences, and provide analytics that help us improve our services.</p>
    </Section>

    <Section title="2. Types of Cookies We Use">
      <p><strong>Essential Cookies:</strong> These cookies are necessary for the platform to function properly. They enable core features such as security, authentication, and session management. You cannot opt out of these cookies.</p>
      <p><strong>Functional Cookies:</strong> These cookies remember your preferences (such as language selection, theme preference) to provide a personalized experience. They are session-persistent and improve usability.</p>
      <p><strong>Analytics Cookies:</strong> We use analytics cookies to understand how visitors interact with our platform. This helps us improve the user experience. The data collected is aggregated and anonymous.</p>
      <p><strong>Performance Cookies:</strong> These cookies help us understand which pages are most visited and identify any errors, enabling us to optimize platform performance.</p>
    </Section>

    <Section title="3. Third-Party Cookies">
      <p>Some cookies on our platform are set by third-party services, including:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li><strong>PostHog:</strong> Used for product analytics and session recording to improve user experience</li>
        <li><strong>Google Fonts:</strong> Used to load custom fonts for the platform interface</li>
        <li><strong>CDN Providers:</strong> Used to deliver static assets efficiently</li>
      </ul>
      <p>These third parties have their own privacy policies governing how they use cookies. We have no control over third-party cookies.</p>
    </Section>

    <Section title="4. Managing Cookies">
      <p>You can control and manage cookies in several ways:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies through their settings menu</li>
        <li><strong>Opt-Out Tools:</strong> You can opt out of analytics tracking via browser extensions</li>
        <li><strong>Platform Settings:</strong> Certain non-essential cookies can be managed in your account settings</li>
      </ul>
      <p>Please note that disabling certain cookies may affect the functionality of our platform. Essential cookies cannot be disabled as they are required for core functionality.</p>
    </Section>

    <Section title="5. Cookie Duration">
      <p><strong>Session Cookies:</strong> These are temporary cookies that expire when you close your browser.</p>
      <p><strong>Persistent Cookies:</strong> These remain on your device for a specified period (typically 30 days to 2 years) or until you delete them manually.</p>
    </Section>

    <Section title="6. Updates to This Policy">
      <p>We may update this Cookie Policy periodically. We will notify you of any significant changes by posting the updated policy on this page with a new effective date.</p>
    </Section>

    <Section title="7. Contact Us">
      <p>If you have questions about our use of cookies, please contact us at <strong>support@quidory.com</strong>.</p>
      <p>
        <strong className="text-foreground">Legal Entity:</strong> Al Yakhour LLC<br />
        <strong className="text-foreground">Registered Office:</strong> Wyoming, USA<br />
        <strong className="text-foreground">Website:</strong> https://quidory.com
      </p>
    </Section>
  </LegalPage>
);

export default CookiePage;
